import { useEffect, useRef, useState } from 'react';

import { useEngineBus } from '../hooks/useEngineBus.js';

/**
 * @file Top-right wanted meter: five star slots, GTA-style.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * BUS CONTRACT — `'wanted:level'`
 * ─────────────────────────────────────────────────────────────────────────────
 * ```
 * bus.emit('wanted:level', { level: 0..5, flash?: boolean })
 * ```
 *  • `level` is clamped to 0..5 here, so a system that overshoots cannot break
 *    the layout.
 *  • `flash` is optional and means "replay the pop even though the level did
 *    not go up" — a re-spot after the meter had started cooling down.
 *
 * The wanted SYSTEM does not exist yet (see CLAUDE.md: the police/gang factions
 * are the next job). This component is built to the contract above and is
 * INVISIBLE until the first event ever arrives, so it costs nothing until then.
 * `?wanted=N` forces it visible at level N for visual review without a system.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE ANIMATION IS KEYED, NOT TOGGLED — AND THE COUNTER IS PER STAR
 * ─────────────────────────────────────────────────────────────────────────────
 * A CSS animation that has already run sits at its end state forever; setting
 * the same class again does nothing. So each star's `key` carries its OWN pop
 * counter — React unmounts and remounts that one node and the browser replays
 * `star-pop` from frame zero. Only the stars between the old level and the new
 * one are bumped, because re-popping the ones that were already lit reads as
 * the whole meter resetting.
 *
 * ⚠ One shared counter does NOT work here, and the failure is silent: the
 * `setState` that bumps it re-renders immediately, at which point "which stars
 * are new" has already been recomputed from the NEW previous level, the key
 * flips back, and the star remounts without the class a frame after it got it.
 * Per-star counters are monotonic, so no re-render can take a pop away.
 *
 * @param {Object} props
 * @param {import('../../engine/core/Engine.js').Engine|null} props.engine
 */

const SLOTS = 5;

/** `?wanted=N` → force-show at N (dev affordance, see the header). */
function forcedLevel() {
  if (typeof window === 'undefined') return null;
  const raw = new URLSearchParams(window.location.search).get('wanted');
  if (raw == null) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? Math.max(0, Math.min(SLOTS, Math.round(n))) : null;
}

export default function WantedStars({ engine }) {
  const [forced] = useState(forcedLevel);
  const payload = useEngineBus(engine, 'wanted:level', { throttle: 80 });

  /** One monotonic pop counter per slot — see the header. */
  const [pops, setPops] = useState(() => new Array(SLOTS).fill(0));
  const prevRef = useRef(0);

  const live = payload == null ? null : clampLevel(payload);
  const level = live ?? forced ?? 0;

  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = level;
    // A drop never pops. An increase pops only the newly-lit slots. `flash`
    // pops the whole lit run without a level change (a re-spot).
    const from = level > prev ? prev : payload?.flash && level > 0 ? 0 : -1;
    if (from < 0) return;
    setPops((old) => {
      const next = old.slice();
      for (let i = from; i < level; i++) next[i] += 1;
      return next;
    });
  }, [level, payload]);

  // Hidden until the wanted system says something — or until `?wanted=N`.
  if (live == null && forced == null) return null;

  return (
    <div
      className={`pointer-events-none absolute right-3 top-14 select-none ${level >= 3 ? 'wanted-pulse' : ''}`}
      role="status"
      aria-label={`Wanted level ${level} of ${SLOTS}`}
    >
      <div className="flex items-center gap-1 rounded-md border border-hairline bg-void/70 px-2 py-1.5 backdrop-blur-sm">
        {Array.from({ length: SLOTS }, (_, i) => {
          const on = i < level;
          return <Star key={`${i}:${on ? 'on' : 'off'}:${pops[i]}`} on={on} pop={on && pops[i] > 0} />;
        })}
      </div>
    </div>
  );
}

/**
 * @param {any} payload
 * @returns {number} 0..5
 */
function clampLevel(payload) {
  const raw = typeof payload === 'number' ? payload : payload?.level;
  const n = Number(raw);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(SLOTS, Math.round(n)));
}

/**
 * One slot. An SVG star rather than the ★ glyph: a font-dependent glyph is a
 * different shape and a different optical weight on every machine, and this is
 * the one HUD element the player counts at a glance.
 *
 * @param {{on: boolean, pop: boolean}} props
 */
function Star({ on, pop }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="19"
      height="19"
      className={pop ? 'star-pop' : undefined}
      style={{ display: 'block' }}
      aria-hidden="true"
    >
      <path
        d="M12 2.6 14.9 9l6.9.7-5.2 4.6 1.5 6.8L12 17.6 5.9 21.1l1.5-6.8L2.2 9.7 9.1 9z"
        fill={on ? '#ffc10a' : 'transparent'}
        stroke={on ? '#7a4a00' : '#3a4150'}
        strokeWidth={on ? 1.1 : 1.4}
        strokeLinejoin="round"
      />
    </svg>
  );
}
